import type {
  AdminQuestCompletionEvent,
  QuestCompletionEventOutcome,
  QuestCompletionEventTier,
} from '@src/dto/QuestCompletionEvents';
import { useStore } from '@tanstack/react-store';
import type { SortingState } from '@tanstack/react-table';
import { ArrowLeft, Coins, Eye } from 'lucide-react';
import { use, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { DataTable } from '../../components/common/DataTable';
import { FilterBar, SelectFilter } from '../../components/common/FilterBar';
import { ManagementPageLayout } from '../../components/management/ManagementPageLayout';
import { Pagination } from '../../components/common/Pagination';
import { Button } from '../../components/ui/button';
import { TRPCContext } from '../../contexts/TRPC';
import { useAsyncData } from '../../hooks/useAsyncData';
import { useManagementCrud } from '../../hooks/useManagementCrud';
import { usePageTitle } from '../../hooks/usePageTitle';
import type { QueryLoadContext } from '../../hooks/usePaginatedQueryLoader';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import { isLeftResult, isRightResult } from '../../lib/trpcTypeGuards';
import {
  applyQuestEventsUrlState,
  questCompletionEventsStore,
  selectEvent,
  setEvents,
  setFilters,
  setLoading,
  setPage,
  setPaginationInfo,
  setSorting,
  updateFilter,
} from '../../stores/questCompletionEventsStore';
import type { RightResult, RouterOutputs } from '../../types/TRPC';
import { captureAndReportError } from '../../utils/errorReporting';
import {
  buildQuestEventsParams,
  parseQuestEventsUrlState,
  QUEST_EVENTS_URL_KEYS,
} from '../../utils/questEventsUrlState';
import { canAccessAdminPages } from '../../utils/roleUtils';
import { formatDateTimeWithLocalZone } from '../../utils/stringHelpers';
import {
  mergeOwnedParams,
  ownedParamsSignature,
} from '../../utils/urlSearchParamSync';
import { QuestEventDetailModal } from './QuestEventDetailModal';

type EventSortId = 'completedAt' | 'created' | 'walletId';

const TIER_BADGE_CLASSES: Record<QuestCompletionEventTier, string> = {
  Easy: 'bg-green-500/10 text-green-400 border-green-500/20',
  Deep: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
};

const OUTCOME_BADGE: Record<
  QuestCompletionEventOutcome,
  { label: string; className: string }
> = {
  completed: {
    label: 'Awarded',
    className: 'bg-green-500/10 text-green-400 border-green-500/20',
  },
  recorded: {
    label: 'Not awarded',
    className: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  },
  already_completed: {
    label: 'Already completed',
    className: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20',
  },
};

const TIER_FILTER_OPTIONS = [
  { value: 'all', label: 'All tiers' },
  { value: 'Easy', label: 'Easy' },
  { value: 'Deep', label: 'Deep' },
];

const COMPLETION_VIEW_OPTIONS = [
  { value: 'awarded', label: 'Awarded only' },
  { value: 'all', label: 'All completions' },
];

interface FilterData {
  seasons: { id: string; label: string }[];
  rounds: { id: string; seasonId: string; roundNumber: string }[];
  apps: { id: string; roundId: string; appName: string }[];
  quests: { id: string; roundAppId: string; title: string }[];
  credentials: { id: string; label: string; keyPrefix: string }[];
}

const EMPTY_FILTER_DATA: FilterData = {
  seasons: [],
  rounds: [],
  apps: [],
  quests: [],
  credentials: [],
};

interface EventsLoadContext {
  filters: {
    seasonId: string;
    roundId: string;
    roundAppId: string;
    questId: string;
    tier: string;
    completionView: string;
    developerCredentialId: string;
    search: string;
  };
  sorting: SortingState;
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
  };
}

function TierBadge({ tier }: { tier: QuestCompletionEventTier }) {
  return (
    <span
      className={`px-2 py-0.5 rounded text-xs font-medium border ${TIER_BADGE_CLASSES[tier]}`}
    >
      {tier}
    </span>
  );
}

function OutcomeBadge({ outcome }: { outcome: QuestCompletionEventOutcome }) {
  const badge = OUTCOME_BADGE[outcome];
  return (
    <span
      className={`px-2 py-0.5 rounded text-xs font-medium border whitespace-nowrap ${badge.className}`}
    >
      {badge.label}
    </span>
  );
}

export function QuestEventsPage() {
  const { authState, canRender } = useRequireAuth({ requireRole: 'admin' });
  const trpc = use(TRPCContext);
  usePageTitle('Quest Completion Events | Admin');

  const [searchParams, setSearchParams] = useSearchParams();
  const searchKey = searchParams.toString();
  // Set right before we write the URL ourselves so the hydrate effect that the
  // write triggers doesn't echo straight back into another write.
  const skipNextUrlWriteRef = useRef(false);

  const events = useStore(questCompletionEventsStore, (state) => state.events);
  const selectedEventId = useStore(
    questCompletionEventsStore,
    (state) => state.ui.selectedEventId,
  );

  const selectedEvent =
    events.find((event) => event.id === selectedEventId) ?? null;

  const { data: filterData, run: loadFilterData } = useAsyncData<FilterData>({
    initialData: EMPTY_FILTER_DATA,
  });

  // URL → store. Runs on mount and on any URL change (incl. browser
  // back/forward). The store is a module-level singleton, so hydrating from the
  // URL also clears any stale filters/selection left over from a prior visit —
  // which is why no explicit mount reset is needed. applyQuestEventsUrlState is
  // a no-op when nothing differs, so our own writes don't loop.
  useLayoutEffect(() => {
    skipNextUrlWriteRef.current = true;
    applyQuestEventsUrlState(
      parseQuestEventsUrlState(new URLSearchParams(searchKey)),
    );
  }, [searchKey]);

  useEffect(() => {
    if (!canRender) {
      return;
    }
    loadFilterData(() => trpc.client.getQuestWorkspace.query(), {
      onSuccess: (result, helpers) => {
        if (isRightResult(result)) {
          const workspace = result.right as RightResult<
            RouterOutputs['getQuestWorkspace']
          >;
          helpers.setData({
            seasons: workspace.seasons.map((season, index) => ({
              id: season.id,
              label: `Season ${index + 1}`,
            })),
            rounds: workspace.rounds.map((round) => ({
              id: round.id,
              seasonId: round.seasonId,
              roundNumber: round.roundNumber,
            })),
            apps: workspace.apps.map((app) => ({
              id: app.id,
              roundId: app.roundId,
              appName: app.appName,
            })),
            quests: workspace.quests.map((quest) => ({
              id: quest.id,
              roundAppId: quest.roundAppId,
              title: quest.title,
            })),
            credentials: workspace.credentials.map((credential) => ({
              id: credential.id,
              label: credential.label,
              keyPrefix: credential.keyPrefix,
            })),
          });
        } else if (isLeftResult(result)) {
          toast.error(result.left.message);
        }
      },
      onError: (error) => {
        captureAndReportError(error, {
          operation: 'admin.loadQuestWorkspace',
          component: 'QuestEventsPage',
        });
        toast.error('Failed to load quest filters');
      },
    });
  }, [canRender, loadFilterData, trpc]);

  const loadEvents = async ({
    isCurrentRequest,
    filters,
    sorting,
    pagination,
  }: QueryLoadContext & EventsLoadContext) => {
    try {
      const result = await trpc.client.listQuestCompletionEvents.query({
        filters: {
          seasonId: filters.seasonId || undefined,
          roundId: filters.roundId || undefined,
          roundAppId: filters.roundAppId || undefined,
          questId: filters.questId || undefined,
          tier:
            filters.tier === 'all'
              ? undefined
              : (filters.tier as QuestCompletionEventTier),
          developerCredentialId: filters.developerCredentialId || undefined,
          search: filters.search || undefined,
          includeUnawarded: filters.completionView === 'all' || undefined,
        },
        pagination: { page: pagination.currentPage, limit: 20 },
        sorting:
          sorting.length > 0
            ? sorting.map((sort) => ({
                id: sort.id as EventSortId,
                desc: sort.desc ?? false,
              }))
            : undefined,
      });

      if (!isCurrentRequest()) {
        return;
      }

      if (isRightResult(result)) {
        setEvents(result.right.events);
        setPaginationInfo(
          pagination.currentPage,
          result.right.pagination.totalPages,
          result.right.pagination.total,
        );
      } else if (isLeftResult(result)) {
        toast.error(result.left.message);
      }
    } catch (error) {
      if (!isCurrentRequest()) {
        return;
      }
      captureAndReportError(error, {
        operation: 'admin.listQuestCompletionEvents',
        component: 'QuestEventsPage',
        filters,
        page: pagination.currentPage,
      });
      toast.error('Failed to load completion events');
    }
  };

  const { filters, sorting, pagination, isLoading } = useManagementCrud({
    enabled: canRender,
    store: questCompletionEventsStore,
    actions: {
      updateFilter,
      setSorting,
      setPage,
      setPaginationInfo,
      setLoading,
    },
    loadPage: loadEvents,
  });

  // store → URL. Mirrors filters/sorting/page/open-modal back into the query
  // string (replace, so it doesn't spam history) while preserving any params
  // this page doesn't own. Skips the write that the hydrate effect just queued.
  useEffect(() => {
    if (skipNextUrlWriteRef.current) {
      skipNextUrlWriteRef.current = false;
      return;
    }
    const desired = buildQuestEventsParams({
      filters,
      sorting,
      page: pagination.currentPage,
      selectedEventId,
    });
    // Compare canonical-vs-canonical: the URL's owned params run back through
    // parse→build so a non-canonical-but-equivalent URL (an invalid value, an
    // explicit default, or an empty param) isn't seen as different from the
    // state we'd write, which would otherwise trigger a redundant write.
    const current = buildQuestEventsParams(
      parseQuestEventsUrlState(searchParams),
    );
    if (
      ownedParamsSignature(current, QUEST_EVENTS_URL_KEYS) ===
      ownedParamsSignature(desired, QUEST_EVENTS_URL_KEYS)
    ) {
      return;
    }
    setSearchParams(
      mergeOwnedParams(searchParams, QUEST_EVENTS_URL_KEYS, desired),
      { replace: true },
    );
  }, [
    filters,
    sorting,
    pagination.currentPage,
    selectedEventId,
    searchParams,
    setSearchParams,
  ]);

  const seasonOptions = useMemo(
    () => [
      { value: '', label: 'All seasons' },
      ...filterData.seasons.map((season) => ({
        value: season.id,
        label: season.label,
      })),
    ],
    [filterData.seasons],
  );

  const seasonLabelById = useMemo(
    () =>
      new Map(filterData.seasons.map((season) => [season.id, season.label])),
    [filterData.seasons],
  );

  const roundOptions = useMemo(() => {
    const rounds = filters.seasonId
      ? filterData.rounds.filter((round) => round.seasonId === filters.seasonId)
      : filterData.rounds;
    return [
      { value: '', label: 'All rounds' },
      ...rounds.map((round) => ({
        value: round.id,
        label: `Round #${round.roundNumber}`,
      })),
    ];
  }, [filterData.rounds, filters.seasonId]);

  const appOptions = useMemo(() => {
    if (!filters.roundId) {
      return [{ value: '', label: 'Select a round first' }];
    }
    return [
      { value: '', label: 'All apps' },
      ...filterData.apps
        .filter((app) => app.roundId === filters.roundId)
        .map((app) => ({ value: app.id, label: app.appName })),
    ];
  }, [filterData.apps, filters.roundId]);

  const questOptions = useMemo(() => {
    if (!filters.roundAppId) {
      return [{ value: '', label: 'Select an app first' }];
    }
    return [
      { value: '', label: 'All quests' },
      ...filterData.quests
        .filter((quest) => quest.roundAppId === filters.roundAppId)
        .map((quest) => ({ value: quest.id, label: quest.title })),
    ];
  }, [filterData.quests, filters.roundAppId]);

  const credentialOptions = useMemo(
    () => [
      { value: '', label: 'All credentials' },
      ...filterData.credentials.map((credential) => ({
        value: credential.id,
        label: credential.keyPrefix
          ? `${credential.label} (${credential.keyPrefix})`
          : credential.label,
      })),
    ],
    [filterData.credentials],
  );

  if (!canRender) {
    return null;
  }

  const columns = [
    {
      key: 'walletId',
      header: 'Wallet',
      size: 200,
      enableSorting: true,
      accessor: (event: AdminQuestCompletionEvent) => (
        <span className="font-mono text-xs text-white break-all">
          {event.walletId}
        </span>
      ),
    },
    {
      key: 'round',
      header: 'Season / Round',
      size: 200,
      enableSorting: false,
      accessor: (event: AdminQuestCompletionEvent) => (
        <div className="space-y-0.5">
          <div className="text-sm text-white truncate">
            {seasonLabelById.get(event.seasonId) ?? 'Season'}
          </div>
          <div className="text-xs text-neutral-400 truncate">
            Round #{event.roundNumber}
          </div>
        </div>
      ),
    },
    {
      key: 'app',
      header: 'App',
      size: 180,
      enableSorting: false,
      accessor: (event: AdminQuestCompletionEvent) => (
        <div className="space-y-0.5">
          <div className="text-sm text-white truncate">{event.appName}</div>
          <div className="font-mono text-xs text-neutral-500 truncate">
            {event.androidPackage}
          </div>
        </div>
      ),
    },
    {
      key: 'quest',
      header: 'Quest',
      size: 220,
      enableSorting: false,
      accessor: (event: AdminQuestCompletionEvent) => (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-sm text-white truncate">
              {event.questTitle}
            </span>
            <TierBadge tier={event.questTier} />
          </div>
          <div className="font-mono text-xs text-neutral-500 truncate">
            {event.questKey}
          </div>
        </div>
      ),
    },
    {
      key: 'credential',
      header: 'Credential',
      size: 160,
      enableSorting: false,
      accessor: (event: AdminQuestCompletionEvent) =>
        event.credentialLabel ? (
          <div className="space-y-0.5">
            <div className="text-sm text-white truncate">
              {event.credentialLabel}
            </div>
            {event.credentialKeyPrefix ? (
              <div className="font-mono text-xs text-neutral-500 truncate">
                {event.credentialKeyPrefix}
              </div>
            ) : null}
          </div>
        ) : (
          <span className="text-neutral-500">—</span>
        ),
    },
    {
      key: 'completedAt',
      header: 'Reported',
      size: 190,
      enableSorting: true,
      sortDescFirst: true,
      accessor: (event: AdminQuestCompletionEvent) => (
        <span className="text-xs text-neutral-400">
          {formatDateTimeWithLocalZone(event.completedAt)}
        </span>
      ),
    },
    {
      key: 'outcome',
      header: 'Outcome',
      size: 140,
      enableSorting: false,
      accessor: (event: AdminQuestCompletionEvent) => (
        <OutcomeBadge outcome={event.outcome} />
      ),
    },
    {
      key: 'actions',
      header: 'Details',
      size: 90,
      enableSorting: false,
      accessor: (event: AdminQuestCompletionEvent) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            selectEvent(event.id);
          }}
          className="text-teal-400 transition-colors hover:text-teal-300"
          title="View details"
        >
          <Eye className="size-4" />
        </button>
      ),
    },
  ];

  return (
    <>
      <ManagementPageLayout
        authState={authState}
        canAccess={canAccessAdminPages}
      >
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <Link
                to="/quests"
                className="inline-flex items-center gap-1.5 text-xs text-neutral-400 transition-colors hover:text-white"
              >
                <ArrowLeft className="size-3.5" />
                Back to quests
              </Link>
              <h1 className="text-2xl font-semibold text-white">
                Quest Completion Events
              </h1>
              <p className="text-sm text-neutral-400">
                Every quest completion reported by partner apps, across seasons
                and rounds.
              </p>
            </div>
            <Button
              asChild
              type="button"
              variant="outline"
              className="border-neutral-700 bg-neutral-800/60 text-neutral-200 hover:bg-neutral-700/60"
            >
              <Link to="/quests/claims">
                <Coins className="size-4" />
                View claims
              </Link>
            </Button>
          </div>

          <FilterBar
            searchValue={filters.search}
            onSearchChange={(value) => updateFilter('search', value)}
            searchPlaceholder="Search by wallet or request ID…"
          >
            <SelectFilter
              value={filters.seasonId}
              onChange={(value) =>
                setFilters({
                  seasonId: value,
                  roundId: '',
                  roundAppId: '',
                  questId: '',
                })
              }
              options={seasonOptions}
            />
            <SelectFilter
              value={filters.roundId}
              onChange={(value) =>
                setFilters({ roundId: value, roundAppId: '', questId: '' })
              }
              options={roundOptions}
            />
            <SelectFilter
              value={filters.roundAppId}
              onChange={(value) =>
                setFilters({ roundAppId: value, questId: '' })
              }
              options={appOptions}
            />
            <SelectFilter
              value={filters.questId}
              onChange={(value) => updateFilter('questId', value)}
              options={questOptions}
            />
            <SelectFilter
              value={filters.tier}
              onChange={(value) => updateFilter('tier', value)}
              options={TIER_FILTER_OPTIONS}
            />
            <SelectFilter
              value={filters.completionView}
              onChange={(value) => updateFilter('completionView', value)}
              options={COMPLETION_VIEW_OPTIONS}
            />
            <SelectFilter
              value={filters.developerCredentialId}
              onChange={(value) => updateFilter('developerCredentialId', value)}
              options={credentialOptions}
            />
          </FilterBar>

          <div className="bg-neutral-800/50 rounded-lg border border-neutral-700/50 overflow-hidden shadow-lg">
            <div className="px-6 py-4 border-b border-neutral-700/50">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-white">
                    {pagination.total}{' '}
                    {pagination.total === 1 ? 'Event' : 'Events'}
                  </h3>
                  <p className="text-xs text-neutral-400">
                    {filters.completionView === 'all'
                      ? 'All completion reports, including those that did not award a badge.'
                      : 'Badge-awarded completion records. One per wallet per quest.'}
                  </p>
                </div>
                {pagination.totalPages > 1 && (
                  <div className="text-xs text-neutral-500">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </div>
                )}
              </div>
            </div>

            <DataTable
              data={events}
              columns={columns}
              isLoading={isLoading}
              emptyMessage="No completion events found"
              enableSorting={true}
              sorting={sorting}
              onSortingChange={(updater) => setSorting(updater)}
              getRowId={(event) => event.id}
              onRowClick={(event) => selectEvent(event.id)}
              renderMobileItem={(event) => (
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <span className="font-mono text-xs text-white break-all">
                      {event.walletId}
                    </span>
                    <OutcomeBadge outcome={event.outcome} />
                  </div>
                  <div className="text-xs text-neutral-400">
                    {seasonLabelById.get(event.seasonId) ?? 'Season'} • Round #
                    {event.roundNumber}
                  </div>
                  <div className="text-sm text-white">
                    {event.appName}
                    <span className="flex items-center gap-2 text-xs text-neutral-400">
                      {event.questTitle}
                      <TierBadge tier={event.questTier} />
                    </span>
                  </div>
                  <div className="text-xs text-neutral-400">
                    {formatDateTimeWithLocalZone(event.completedAt)}
                  </div>
                  <div className="flex justify-end border-t border-neutral-700/50 pt-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        selectEvent(event.id);
                      }}
                      className="flex items-center gap-1 text-xs font-medium text-teal-400 hover:text-teal-300"
                    >
                      <Eye className="size-3.5" />
                      Details
                    </button>
                  </div>
                </div>
              )}
            />

            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>
      </ManagementPageLayout>

      <QuestEventDetailModal
        isOpen={selectedEvent !== null}
        event={selectedEvent}
        onClose={() => selectEvent(null)}
      />
    </>
  );
}
